import clinicAPI from "../../../services/clinicApi";
import { formatTimeRu } from "../../../utils/appointmentLabels";
import { eventKindLabel } from "../../../constants/calendarSessionTypes";
import s from "../clinic.module.css";
import {
  attendanceMarkState,
  canMarkSessionAttendance,
  isIndividualSession,
  isEventSession,
} from "./calendarUtils";

function sessionParts(sess) {
  if (isEventSession(sess)) {
    const kind = eventKindLabel(sess.event_kind);
    return {
      title: sess.title || kind,
      subtitle: [kind, sess.branch?.name].filter(Boolean).join(" · "),
    };
  }
  if (isIndividualSession(sess)) {
    const name =
      sess.individualClient?.full_name ||
      sess.individual_client?.full_name ||
      "Клиент";
    return {
      title: `Индивидуально · ${name}`,
      subtitle: [sess.branch?.name].filter(Boolean).join(" · "),
    };
  }
  return {
    title: sess.group?.name || "Группа",
    subtitle: [sess.group?.sport_direction, sess.branch?.name]
      .filter(Boolean)
      .join(" · "),
  };
}

function AttendBadge({
  present,
  expected,
  marked,
  absent,
  complete,
  needsAttention,
}) {
  if (!expected) return null;

  if (needsAttention) {
    const unmarked = Math.max(0, expected - marked);
    return (
      <span
        className={s.calSessionBadge}
        data-tone="warn"
        title={`Не отмечено: ${unmarked} из ${expected}`}
      >
        ! {unmarked}
      </span>
    );
  }

  const allMarked = complete || marked >= expected;
  const allPresent = present >= expected;

  if (allMarked && allPresent) {
    return (
      <span className={s.calSessionBadge} data-tone="done" title="Все были">
        ✓ {expected}
      </span>
    );
  }

  if (allMarked && present < expected) {
    const miss = absent > 0 ? absent : expected - present;
    return (
      <span
        className={s.calSessionBadge}
        data-tone="partial"
        title={`Было: ${present}, не было: ${miss}`}
      >
        {present}/{expected}
      </span>
    );
  }

  const unmarked = Math.max(0, expected - marked);
  const title = unmarked
    ? `Было: ${present}, не отмечено: ${unmarked}`
    : `Было: ${present} из ${expected}`;

  return (
    <span className={s.calSessionBadge} data-tone="progress" title={title}>
      {present}/{expected}
    </span>
  );
}

export default function SessionCalendarCard({
  sess,
  color,
  onClick,
  onMarkAll,
  variant = "timeline",
  active = false,
}) {
  const expected = sess.expected_count ?? 0;
  const present = sess.present_count ?? 0;
  const marked = sess.marked_count ?? 0;
  const absent = sess.absent_count ?? 0;
  const isEvent = isEventSession(sess);
  const complete =
    sess.attendance_complete ??
    (expected > 0 && marked >= expected);
  const needsAttention = sess.needs_attendance;
  const locked = sess.status === "cancelled";
  const completed = sess.status === "completed";
  const markable = canMarkSessionAttendance(sess);
  const { title, subtitle } = sessionParts(sess);

  const handleMarkAll = (e) => {
    e.stopPropagation();
    onMarkAll?.(sess);
  };

  const isTimeline = variant === "timeline";

  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        s.calSessionCard,
        isTimeline ? s.calSessionCardTimeline : s.calSessionCardGrid,
        active ? s.calSessionCardActive : "",
        needsAttention ? s.calSessionCardNeedsAttend : "",
        completed ? s.calSessionCardCompleted : "",
        locked ? s.calSessionCardLocked : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ "--session-color": color }}
      onClick={() => onClick?.(sess)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(sess);
        }
      }}
    >
      <span className={s.calSessionCardAccent} aria-hidden />
      <div className={s.calSessionCardBody}>
        <div className={s.calSessionCardRow}>
          <span className={s.calSessionCardTime}>
            {formatTimeRu(sess.start_datetime)}
            {!isEvent ? (
              <>
                <span className={s.calSessionCardTimeSep}>–</span>
                {formatTimeRu(sess.end_datetime)}
              </>
            ) : null}
          </span>
          {!isEvent ? (
            <AttendBadge
              present={present}
              expected={expected}
              marked={marked}
              absent={absent}
              complete={complete}
              needsAttention={needsAttention}
            />
          ) : null}
        </div>
        <p className={s.calSessionCardTitle}>{title}</p>
        {subtitle ? (
          <p className={s.calSessionCardSub}>{subtitle}</p>
        ) : null}
      </div>
      {!isTimeline &&
      !isEvent &&
      expected > 0 &&
      marked < expected &&
      onMarkAll &&
      !locked &&
      markable ? (
        <button
          type="button"
          className={s.calSessionMarkAll}
          onClick={handleMarkAll}
          title="Отметить всех как «был»"
        >
          Все ✓
        </button>
      ) : null}
    </div>
  );
}

export async function markSessionAllPresent(sessionId) {
  const data = await clinicAPI.getSessionJournal(sessionId);
  if (!canMarkSessionAttendance(data.session)) {
    const err = new Error("Занятие отменено");
    err.code = "ATTENDANCE_NOT_OPEN";
    throw err;
  }
  const pending = (data.session?.attendances || []).filter(
    (a) => !attendanceMarkState(a).isPresent,
  );
  for (const a of pending) {
    await clinicAPI.markSessionAttendance(sessionId, {
      patient_id: a.patient_id,
      status: "present",
    });
  }
}
